import { z } from 'zod';
import { BarChart } from '@/components/charts/BarChart';
import { barChartConfigSchema } from '@/components/charts/chartConfig';
import { MainTitle, TextBloc } from '@/components/TextBlock';
import { TitleColumn } from '@/components/TitleColumn';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { fontOptionsSchema } from '@/lib/fontStyle';
import {
  placeholderText,
  substituteVariables,
  textReplacementSchema,
  type TextReplacement,
} from '@/lib/text';
import { collapsesTitleColumn } from '@/lib/titleColumn';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * Hour-by-hour occupancy as pentagon bars under a safety-line threshold. Port of
 * `hourlyChart/hourlyChartWidget.vue`.
 *
 * Used by four deployed configs (`corio`, `freshwaves`, `hbr_tcc_2`, `hbr_tcc_3`), all twelve
 * labels wide (`8am`..`7pm`) and all carrying `"slide": 1` — so on a config with a slider they
 * belong to the first slide, which is SYM-492's concern, not this widget's.
 */

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  mainTitle: z.string().optional(),
  mainTitleFont: fontOptionsSchema.optional(),
  title: z.string().optional(),
  titleFont: fontOptionsSchema.optional(),
  textBloc: z
    .object({
      text: z.string().default(''),
      /** [sic] The spelling `hourlyChartWidget.vue` reads, and the one no config carries. */
      replaced: z.array(textReplacementSchema).optional(),
      /** The `TextWidget`/`DoughnutWidget` spelling, read too — docs/MIGRATION-WIDGETS.md §3. */
      replace: z.array(textReplacementSchema).optional(),
    })
    .prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
  chartConfig: barChartConfigSchema.prefault({}),
});

/** docs/MIGRATION-WIDGETS.md §7 — `values` plus whatever `{{variables}}` the text needs. */
const payloadSchema = z.looseObject({
  values: z.array(z.number()).optional(),
});

type HourlyPayload = z.infer<typeof payloadSchema>;

export function HourlyChartWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `hourly-chart-widget-options:${componentId}`,
      '[signage] HourlyChartWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const payload = readPayload(data, componentId);

  // Legacy assigned `values.value = data.value.values` unconditionally, so a payload without
  // `values` blanked the chart's data to `undefined`. Falling back to the zero row keeps the
  // axis drawn (CLAUDE.md constraint 4).
  const values = payload?.values ?? zeroRow(opt.chartConfig.labels.length);

  const rightColumnClass = collapsesTitleColumn(opt.title, opt.textBloc.text)
    ? 'chart-container w-full pt-2 lg:pt-0'
    : 'chart-container w-full pt-2 lg:w-1/2 lg:pt-0';

  return (
    <div className={className} style={style}>
      <MainTitle text={opt.mainTitle ?? ''} font={opt.mainTitleFont} />
      <div className="w-full flex flex-col lg:flex-row lg:flex-wrap">
        <TitleColumn className="w-full lg:w-1/2" title={opt.title} titleFont={opt.titleFont}>
          <TextBloc text={resolveText(opt.textBloc, payload)} font={opt.textBlocFont} />
        </TitleColumn>
        <div className={rightColumnClass}>
          <BarChart values={values} config={opt.chartConfig} />
        </div>
      </div>
    </div>
  );
}

/**
 * docs/MIGRATION-WIDGETS.md §9: one `0` per configured label before the first message — not an
 * empty chart, so the axis is drawn at its final width and the bars grow into it.
 *
 * Built fresh on each call rather than memoised, which looks wasteful for a value that is the
 * chart's `data` prop — `react-chartjs-2` calls `chart.update()` whenever `data.datasets`
 * changes identity. It is not, because of *when* this value is used: the widget only re-renders
 * when its own message changes (Zustand selects per component id), and from the first message
 * onwards the payload's `values` are used instead. A new array here therefore never reaches a
 * second render of the same chart.
 *
 * Not a shared module-level constant either, deliberately: Chart.js attaches a `_chartjs`
 * property to whatever array it is given so it can observe mutations, so one array shared
 * between two chart instances is shared *state*, not a shared constant.
 */
function zeroRow(length: number): number[] {
  return Array.from({ length }, () => 0);
}

/** The payload, or `undefined` before the first message / on a shape mismatch. */
function readPayload(data: unknown, componentId: string): HourlyPayload | undefined {
  if (data === undefined) return undefined;

  const payload = payloadSchema.safeParse(data);
  if (!payload.success) {
    warnOnce(
      `hourly-chart-widget-payload:${componentId}`,
      '[signage] HourlyChartWidget payload rejected; keeping the empty state. %s',
      z.prettifyError(payload.error),
    );
    return undefined;
  }
  return payload.data;
}

/**
 * The text block, in `'strip'` mode.
 *
 * This widget is in the second substitution group (docs/MIGRATION-WIDGETS.md §3): an
 * unresolved `{{variable}}` keeps its name and loses its braces, rather than becoming `N/A`.
 * Before the first message every variable renders as `0`, which is the legacy loading state
 * and §9's specified empty state — note that this differs from `DoughnutWidget`, four lines of
 * config away, which renders nothing at all until its feed arrives.
 *
 * `replaced` is read first because it is the key this widget's legacy code reads; `replace` is
 * read as well, per §3. No deployed config carries either on this widget, so neither path
 * changes a display today.
 */
function resolveText(
  textBloc: { text: string; replace?: TextReplacement[]; replaced?: TextReplacement[] },
  payload: HourlyPayload | undefined,
): string {
  if (payload === undefined) return placeholderText(textBloc.text);

  const replace: readonly TextReplacement[] | undefined = textBloc.replaced ?? textBloc.replace;
  return substituteVariables(textBloc.text, payload, { missing: 'strip', replace });
}
